import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { v4 as uuidv4 } from "uuid";

import {
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Dialog,
    DialogActions,
    Button,
    Switch,
    Box,
    useMediaQuery,
    useTheme
} from "@mui/material";

import {
    MoreVert,
    Replay,
    HighlightOff as HighlightOffIcon,
    Undo as UndoIcon,
    SwapHorizOutlined,
    AccountTreeOutlined,
    WhatsApp as WhatsAppIcon
} from "@mui/icons-material";

import { Can } from "../Can";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import NewTicketModal from "../NewTicketModal";
import LinkContactModal from "../LinkContactModal";
import ShowTicketOpen from "../ShowTicketOpenModal";
import ShowTicketLogModal from "../ShowTicketLogModal";
import TicketMessagesDialog from "../TicketMessagesDialog";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import FlowBuilderChatModal from "../FlowBuilderChatModal";

const SessionSchema = Yup.object().shape({
    ratingId: Yup.string().required("Avaliação obrigatória"),
});

const TicketActionButtonsCustom = ({ ticket, contact }) => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const { user } = useContext(AuthContext);
    const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);
    const [open, setOpen] = useState(false);
    const formRef = useRef(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [showTicketLogOpen, setShowTicketLogOpen] = useState(false);
    const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
    const currentContact = contact || ticket.contact;
    const [disableBot, setDisableBot] = useState(currentContact ? currentContact.disableBot : false);
    const [enableIntegration, setEnableIntegration] = useState(ticket.useIntegration);
    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [flowModalOpen, setFlowModalOpen] = useState(false);
    const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { get: getSetting } = useCompanySettings();
    const { getPlanCompany } = usePlans();

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, [ticket, contact]);

    const fetchData = async () => {
        // const companyId = user.companyId;
        // const planConfigs = await getPlanCompany(undefined, companyId);
        setOpenTicketMessageDialog(false);
        if (ticket?.contact) {
            setDisableBot(ticket.contact.disableBot);
        }
        setShowTicketLogOpen(false);
    };

    const handleClickOpen = async (e) => {
        const setting = await getSetting({ "column": "requiredTag" });
        if (setting?.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                } else {
                    setOpen(true);
                }
            } catch (err) {
                toastError(err);
            }
        } else {
            setOpen(true);
        }
    };

    const handleClose = () => {
        if (formRef.current) formRef.current.resetForm();
        setOpen(false);
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleUpdateTicketStatus = async (e, status, userId) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: status,
                userId: userId || null,
            });

            let setting;
            try {
                setting = await getSetting({ "column": "sendGreetingAccepted" });
            } catch (err) {
                toastError(err);
            }

            if (setting?.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") && ticket.status === "pending") {
                handleSendMessage(ticket.id);
            }

            setLoading(false);
            if (status === "open" || status === "group") {
                setCurrentTicket({ ...ticket, code: "#" + status });
                setTimeout(() => { history.push('/tickets'); }, 0);
                setTimeout(() => {
                    history.push(`/tickets/${ticket.uuid}`);
                    setTabOpen(status);
                }, 10);
            } else {
                setCurrentTicket({ id: null, code: null });
                history.push("/tickets");
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleSendMessage = async (id) => {
        let setting;
        try {
            setting = await getSetting({ "column": "greetingAcceptedMessage" });
        } catch (err) {
            toastError(err);
        }
        const msg = `${setting.greetingAcceptedMessage}`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
        }
    };

    const handleDeleteTicket = async () => {
        try {
            await api.delete(`/tickets/${ticket.id}`);
            history.push("/tickets");
        } catch (err) {
            toastError(err);
        }
    };

    const handleContactToggleDisableBot = async () => {
        const { id } = ticket.contact;
        try {
            const { data } = await api.put(`/contacts/toggleDisableBot/${id}`);
            ticket.contact.disableBot = data.disableBot;
            setDisableBot(data.disableBot);
        } catch (err) {
            toastError(err);
        }
    };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, {
                status: ticket.isGroup ? "group" : "open",
                userId: user?.id,
            });
            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user.name);
                    setQueueTicketOpen(otherTicket.data.queue.name);
                    setTabOpen(otherTicket.isGroup ? "group" : "open");
                } else {
                    setLoading(false);
                    setTabOpen(otherTicket.isGroup ? "group" : "open");
                    history.push(`/tickets/${otherTicket.data.uuid}`);
                }
            } else {
                setLoading(false);
                history.push('/tickets');
                setTimeout(() => {
                    history.push(`/tickets/${ticket.uuid}`);
                    setTabOpen(ticket.isGroup ? "group" : "open");
                }, 1000);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleEnableIntegration = async () => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                useIntegration: !enableIntegration
            });
            setEnableIntegration(!enableIntegration);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleCloseTicketWithoutFarewellMsg = async () => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0
            });
            setLoading(false);
            history.push("/tickets");
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={() => setOpenAlert(false)}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {showTicketLogOpen && (
                <ShowTicketLogModal
                    isOpen={showTicketLogOpen}
                    handleClose={() => setShowTicketLogOpen(false)}
                    ticketId={ticket.id}
                />
            )}
            {openTicketMessageDialog && (
                <TicketMessagesDialog
                    open={openTicketMessageDialog}
                    handleClose={() => setOpenTicketMessageDialog(false)}
                    ticketId={ticket.id}
                />
            )}
            {linkModalOpen && (
                <LinkContactModal
                    modalOpen={linkModalOpen}
                    onClose={() => setLinkModalOpen(false)}
                    contactId={ticket.contact.id}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={() => setTransferTicketModalOpen(false)}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}
            {flowModalOpen && (
                <FlowBuilderChatModal
                    open={flowModalOpen}
                    onClose={() => setFlowModalOpen(false)}
                    ticket={ticket}
                />
            )}
            {newTicketModalOpen && (
                <NewTicketModal
                    modalOpen={newTicketModalOpen}
                    initialContact={ticket.contact}
                    onClose={(newTicket) => {
                        setNewTicketModalOpen(false);
                        if (newTicket !== undefined && newTicket.uuid !== undefined) {
                            history.push(`/tickets/${newTicket.uuid}`);
                        }
                    }}
                />
            )}
            {confirmationOpen && (
                <ConfirmationModal
                    title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")} #${ticket.id}?`}
                    open={confirmationOpen}
                    onClose={setConfirmationOpen}
                    onConfirm={handleDeleteTicket}
                >
                    {i18n.t("ticketOptionsMenu.confirmationModal.message")}
                </ConfirmationModal>
            )}

            {/* Action Buttons */}
            {ticket.status === "closed" && (
                <>
                    <ButtonWithSpinner
                        loading={loading}
                        startIcon={<Replay />}
                        size="small"
                        onClick={e => ticket.queueId ? handleAcepptTicket(ticket.id) : setAcceptTicketWithouSelectQueueOpen(true)}
                    >
                        {i18n.t("messagesList.header.buttons.reopen")}
                    </ButtonWithSpinner>
                    <Tooltip title="Criar/Abrir Ticket em outra Conexão">
                        <IconButton
                            size="small"
                            onClick={() => setNewTicketModalOpen(true)}
                            color="primary"
                        >
                            <WhatsAppIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}

            {(ticket.status === "open" || ticket.status === "group") && (
                <>
                    <Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
                        <IconButton onClick={handleClickOpen} color="primary">
                            <HighlightOffIcon />
                        </IconButton>
                    </Tooltip>

                    {!isMobile && (
                        <>
                            <Tooltip title={i18n.t("tickets.buttons.returnQueue")}>
                                <IconButton onClick={(e) => handleUpdateTicketStatus(e, "pending", null)} color="primary">
                                    <UndoIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Transferir Ticket">
                                <IconButton onClick={() => setTransferTicketModalOpen(true)} color="primary">
                                    <SwapHorizOutlined />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Criar/Abrir Ticket em outra Conexão">
                                <IconButton onClick={() => setNewTicketModalOpen(true)} color="primary">
                                    <WhatsAppIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Disparar Fluxo (FlowBuilder)">
                                <IconButton onClick={() => setFlowModalOpen(true)} color="primary">
                                    <AccountTreeOutlined />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={i18n.t("contactModal.form.chatBotContact")}>
                                <Switch
                                    size="small"
                                    checked={disableBot}
                                    onChange={() => handleContactToggleDisableBot()}
                                />
                            </Tooltip>
                        </>
                    )}
                </>
            )}

            {ticket.status === "pending" && (
                <ButtonWithSpinner
                    loading={loading}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={e => ticket.queueId ? handleUpdateTicketStatus(e, "open", user?.id) : setAcceptTicketWithouSelectQueueOpen(true)}
                >
                    {i18n.t("messagesList.header.buttons.accept")}
                </ButtonWithSpinner>
            )}

            <IconButton onClick={handleMenu} color="primary">
                <MoreVert />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={menuOpen}
                onClose={handleCloseMenu}
                PaperProps={{
                    style: {
                        borderRadius: 16,
                    }
                }}
            >
                <MenuItem onClick={() => { setConfirmationOpen(true); handleCloseMenu(); }}>
                    <Can role={user.profile} perform="ticket-options:deleteTicket" yes={() => i18n.t("tickets.buttons.deleteTicket")} />
                </MenuItem>
                <MenuItem onClick={() => { handleEnableIntegration(); handleCloseMenu(); }}>
                    {enableIntegration === true ? i18n.t("messagesList.header.buttons.disableIntegration") : i18n.t("messagesList.header.buttons.enableIntegration")}
                </MenuItem>
                <MenuItem onClick={() => { setShowTicketLogOpen(true); handleCloseMenu(); }}>
                    {i18n.t("messagesList.header.buttons.logTicket")}
                </MenuItem>
                <MenuItem onClick={() => { setOpenTicketMessageDialog(true); handleCloseMenu(); }}>
                    {i18n.t("ticketsList.buttons.exportAsPDF")}
                </MenuItem>

                {(ticket.status === "open" || ticket.status === "group") && isMobile && (
                    <>
                        <MenuItem onClick={(e) => { handleUpdateTicketStatus(e, "pending", null); handleCloseMenu(); }}>
                            {i18n.t("tickets.buttons.returnQueue")}
                        </MenuItem>
                        <MenuItem onClick={() => { setTransferTicketModalOpen(true); handleCloseMenu(); }}>
                            Transferir Ticket
                        </MenuItem>
                        <MenuItem onClick={() => { setNewTicketModalOpen(true); handleCloseMenu(); }}>
                            Criar/Abrir em outra Conexão
                        </MenuItem>
                        <MenuItem onClick={() => { setFlowModalOpen(true); handleCloseMenu(); }}>
                            Disparar Fluxo
                        </MenuItem>
                        <MenuItem onClick={() => { handleContactToggleDisableBot(); }}>
                            {i18n.t("contactModal.form.chatBotContact")}: {disableBot ? "Desativado" : "Ativado"}
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Resolve Dialog */}
            <Formik
                enableReinitialize={true}
                initialValues={{ ratingId: "" }}
                validationSchema={SessionSchema}
                innerRef={formRef}
                onSubmit={(values, actions) => {
                    // handle rating submit if implemented
                }}
            >
                {({ values }) => (
                    <Dialog open={open} onClose={handleClose}>
                        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                onClick={handleCloseTicketWithoutFarewellMsg}
                                variant="contained"
                                sx={{ bgcolor: 'primary.main', color: 'white' }}
                            >
                                {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
                            </Button>
                            <Button
                                onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
                                variant="contained"
                                sx={{ bgcolor: 'primary.main', color: 'white' }}
                            >
                                {i18n.t("messagesList.header.dialogRatingCancel")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Formik>
        </Box>
    );
};

export default TicketActionButtonsCustom;
